// Gemeinsamer Rahmen der vier Personal-Seiten (design_handoff_personal §2): Kopf mit Rollen-Chip, Bereichs-Segment
// (= die vier Routen), Monats-Schritt und Knöpfe der Steuerzeile. Die Steuerzeile nutzt dieselben Handler wie die
// Pille — hier entsteht keine neue Funktion.
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarBlank, CaretLeft, CaretRight, IdentificationCard, LockKey, ShieldCheck, Timer, User, type Icon } from '@phosphor-icons/react';

export type PersonalBereich = 'zeiterfassung' | 'abwesenheiten' | 'personalakte' | 'monatsabschluss';

const BEREICHE: { key: PersonalBereich; label: string; icon: Icon; unter: string; nurVerwalten?: boolean }[] = [
  { key: 'zeiterfassung', label: 'Zeiterfassung', icon: Timer, unter: 'Monatsüberblick, Nachtragen und Anträge' },
  { key: 'abwesenheiten', label: 'Abwesenheiten', icon: CalendarBlank, unter: 'Urlaub, Krankheit und Freigaben' },
  { key: 'personalakte', label: 'Personalakte', icon: IdentificationCard, unter: 'Stammdaten, Nachweise und Dokumente' },
  { key: 'monatsabschluss', label: 'Monatsabschluss', icon: LockKey, unter: 'Prüfen, abschließen und an die Lohnbuchhaltung geben', nurVerwalten: true },
];

/** Kopf + Bereichs-Segment. `verwalten` steuert Rollen-Chip und ob „Monatsabschluss“ erscheint. */
export function PersonalKopf({ bereich, verwalten, mobil }: { bereich: PersonalBereich; verwalten: boolean; mobil: boolean }) {
  const navigate = useNavigate();
  const aktuell = BEREICHE.find((b) => b.key === bereich) ?? BEREICHE[0];
  return (
    <>
      <header className="flex flex-wrap items-end justify-between" style={{ gap: 14 }}>
        <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Personal</span>
          <h1 className="m-0" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02 }}>{aktuell.label}</h1>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>{aktuell.unter}</span>
        </div>
        <span data-tip={verwalten
          ? 'Verwalten – Sie sehen alle Mitarbeitenden, entscheiden Anträge und schließen Monate ab'
          : 'Eigene Sicht – Sie sehen nur Ihre eigenen Zeiten und Anträge'}
          className="inline-flex items-center" style={{ height: 34, padding: '0 14px', gap: 7, borderRadius: 17, fontSize: 12.5, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
          {verwalten ? <ShieldCheck size={14} weight="fill" /> : <User size={14} weight="fill" />}
          {verwalten ? 'Personal · Geschäftsführung' : 'Mitarbeiter-Sicht'}
        </span>
      </header>
      <nav aria-label="Personal-Bereiche" className="immo-glas flex self-start overflow-x-auto"
        style={{ gap: 4, padding: 4, borderRadius: 20, maxWidth: '100%', scrollbarWidth: 'none' }}>
        {BEREICHE.filter((b) => !b.nurVerwalten || verwalten).map((b) => {
          const an = b.key === bereich;
          const I = b.icon;
          return (
            <button key={b.key} type="button" aria-current={an ? 'page' : undefined} data-tip={`${b.label} – ${b.unter}`}
              onClick={() => { if (!an) navigate(`/admin/${b.key}`); }}
              className="inline-flex shrink-0 items-center whitespace-nowrap"
              style={{ height: mobil ? 44 : 40, padding: '0 16px', gap: 8, borderRadius: 16, border: 'none', cursor: an ? 'default' : 'pointer', fontFamily: 'inherit',
                fontSize: 13.5, fontWeight: an ? 800 : 700, background: an ? 'var(--acc-fill)' : 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)',
                boxShadow: an ? 'var(--acc-sh)' : 'none' }}>
              <I size={17} weight={an ? 'fill' : 'bold'} />{b.label}
            </button>
          );
        })}
      </nav>
    </>
  );
}

/** Monat ‹ Oktober 2026 › — dieselben Handler wie der Monats-Schritt in der Pille. */
export function MonatSchritt({ text, onZurueck, onVor, mobil }: { text: string; onZurueck: () => void; onVor: () => void; mobil: boolean }) {
  const k = mobil ? 44 : 36;
  const knopf = { width: k, height: k, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'transparent', color: 'var(--ink)' } as const;
  return (
    <div className="immo-glas inline-flex items-center" style={{ height: 44, padding: '0 4px', gap: 2, borderRadius: 22 }}>
      <button type="button" aria-label="Vorheriger Monat" data-tip="Vorheriger Monat" onClick={onZurueck} className="immo-zeile-hover inline-flex items-center justify-center" style={knopf}>
        <CaretLeft size={15} weight="bold" />
      </button>
      <span className="text-center" style={{ minWidth: 130, fontSize: 14, fontWeight: 800 }}>{text}</span>
      <button type="button" aria-label="Nächster Monat" data-tip="Nächster Monat" onClick={onVor} className="immo-zeile-hover inline-flex items-center justify-center" style={knopf}>
        <CaretRight size={15} weight="bold" />
      </button>
    </div>
  );
}

/** Knopf der Steuerzeile: Glas mit Akzent-Symbol, oder primär (Akzent-Füllung). */
export function SteuerKnopf({ icon: I, children, onClick, tip, primaer = false, gesperrt = false }: {
  icon: Icon; children: ReactNode; onClick: () => void; tip?: string; primaer?: boolean; gesperrt?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} data-tip={tip} disabled={gesperrt}
      className={`${primaer ? '' : 'immo-glas '}inline-flex shrink-0 items-center whitespace-nowrap`}
      style={{ height: 44, padding: primaer ? '0 18px' : '0 16px', gap: 7, borderRadius: 22, border: 'none', cursor: gesperrt ? 'default' : 'pointer', fontFamily: 'inherit',
        fontSize: 13, fontWeight: 800, opacity: gesperrt ? 0.5 : 1,
        background: primaer ? 'var(--acc-fill)' : undefined, color: primaer ? 'var(--acc-ink)' : 'var(--ink)', boxShadow: primaer ? 'var(--acc-sh)' : undefined }}>
      <I size={15} weight="bold" color={primaer ? undefined : 'var(--acc)'} />{children}
    </button>
  );
}

/** Kleiner Chip mit Symbol (Arbeitszeitmodell, Namens-Chip …). */
export function InfoChip({ icon: I, children, farbe = 'var(--acc)', flaeche = 'var(--acc-soft)', tip }: {
  icon: Icon; children: ReactNode; farbe?: string; flaeche?: string; tip?: string;
}) {
  return (
    <span data-tip={tip} className="inline-flex max-w-full items-center" style={{ height: 28, padding: '0 12px', gap: 6, borderRadius: 14, fontSize: 12, fontWeight: 800, background: flaeche, color: farbe }}>
      <I size={13} weight="fill" className="shrink-0" /><span className="truncate">{children}</span>
    </span>
  );
}
